import { useMemo } from 'react';
import { formatValue } from '../support';
export function createCategoryController(config, fragment) {
    const { legend: { controller: legendController }, tooltip: { controller: { category: tooltipController }, }, } = fragment;
    return {
        useProps(props) {
            const legend = legendController.useProps(props.legend);
            // Convert labels to label props
            const originalLabelProps = useMemo(() => props.labels.map((item) => ({
                label: item.text,
            })), [props.labels]);
            // Determine visible datasets
            const visibleDatasets = useMemo(() => props.datasets.filter((dataset) => props.legend.visible.value.includes(dataset.key)), [props.datasets, props.legend.visible.value]);
            // Create an array of indices corresponding to labels
            const indices = useMemo(() => props.labels.map((_, i) => i), [props.labels]);
            // Apply sorting logic
            useMemo(() => {
                const { column, direction } = props.sort;
                if (column === 'category') {
                    // Sort by segment name
                    indices.sort((a, b) => {
                        const A = props.labels[a].text.toLowerCase();
                        const B = props.labels[b].text.toLowerCase();
                        if (A < B)
                            return direction === 'asc' ? -1 : 1;
                        if (A > B)
                            return direction === 'asc' ? 1 : -1;
                        return 0;
                    });
                }
                else if (column === 'value') {
                    // Sort by aggregated value of visible datasets
                    indices.sort((a, b) => {
                        const sumA = visibleDatasets.reduce((acc, ds) => acc + (ds.data[a] ?? 0), 0);
                        const sumB = visibleDatasets.reduce((acc, ds) => acc + (ds.data[b] ?? 0), 0);
                        return direction === 'asc' ? sumA - sumB : sumB - sumA;
                    });
                }
            }, [props.sort, props.labels, visibleDatasets, indices]);
            // Reorder labels and datasets based on the sorted indices
            const sortedLabels = useMemo(() => indices.map((i) => props.labels[i]), [indices, props.labels]);
            const sortedLabelProps = useMemo(() => indices.map((i) => originalLabelProps[i]), [indices, originalLabelProps]);
            const sortedDatasets = useMemo(() => props.datasets.map((ds) => {
                const newData = indices.map((i) => ds.data[i]);
                return { ...ds, data: newData };
            }), [indices, props.datasets]);
            const tooltipContent = tooltipController.useProps({
                datasets: sortedDatasets,
                labels: sortedLabelProps,
                axes: props.axis,
            });
            // Flatten all data values from sorted datasets
            const allValues = useMemo(() => sortedDatasets.flatMap((dataset) => dataset.data), [sortedDatasets]);
            // Filter out null values
            const nonNullValues = useMemo(() => allValues.filter((v) => v !== null), [allValues]);
            const minValue = useMemo(() => {
                // If startAtZero is true, always start at zero
                if (props.axis.scalar.startAtZero) {
                    return 0;
                }
                return nonNullValues.length > 0 ? Math.min(...nonNullValues) : 0;
            }, [nonNullValues, props.axis.scalar.startAtZero]);
            const maxValue = useMemo(() => (nonNullValues.length > 0 ? Math.max(...nonNullValues) : 0), [nonNullValues]);
            const range = maxValue - minValue === 0 ? 1 : maxValue - minValue;
            const finalDatasets = useMemo(() => sortedDatasets
                .filter((item) => props.legend.visible.value.includes(item.key))
                .map((item) => {
                const total = item.data.reduce((acc, value) => acc + value, 0);
                return {
                    ...item,
                    data: item.data.map((value, dataIndex) => {
                        const safeValue = value ?? minValue;
                        const widthPercentage = ((safeValue - minValue) / range) * 100;
                        return {
                            value: value,
                            formattedValue: props.type === 'distribution'
                                ? formatValue(config.strategy.format, 'percentage', value / total, { notation: 'short', decimals: 1 })
                                : formatValue(config.strategy.format, props.axis.scalar.format ?? null, value, { notation: 'short' }),
                            width: `${widthPercentage}%`,
                            container: {
                                onMouseMove(event) {
                                    tooltipContent.trigger.onEnter(event, {
                                        dataset: item,
                                        dataIndex,
                                    });
                                },
                                onMouseLeave(event) {
                                    tooltipContent.trigger.onLeave(event, {
                                        dataset: item,
                                        dataIndex,
                                    });
                                },
                            },
                        };
                    }),
                };
            }), [
                sortedDatasets,
                props.legend.visible.value,
                tooltipContent.trigger,
                minValue,
                range,
                config.strategy.format,
                props.axis.scalar.format,
            ]);
            return {
                fragments: props.fragments,
                legend: legend,
                theme: props.theme,
                tooltip: { ...tooltipContent, Component: props.Tooltip },
                datasets: finalDatasets,
                labels: sortedLabelProps,
                headers: [
                    {
                        key: 'segment',
                        label: 'Segment',
                        sort: props.sort.column === 'category'
                            ? { direction: props.sort.direction }
                            : null,
                    },
                    {
                        key: 'value',
                        label: props.axis.scalar.label ?? 'Value',
                        sort: props.sort.column === 'value'
                            ? { direction: props.sort.direction }
                            : null,
                    },
                ],
            };
        },
    };
}
//# sourceMappingURL=categoryController.js.map